import React from 'react';

import { bemBlock } from '../../modules/bem';
import { Layout } from '../layout/Layout';
import { Text } from '../text/Text';

import { getDataRole } from './Panel.helpers';
import { type DataRoleProps } from './Panel.types';

import './PanelSection.less';

const block = bemBlock('n-Panel');

export type PanelSectionProps = Omit<React.HTMLAttributes<HTMLElement>, 'title'> &
  DataRoleProps & {
    children: React.ReactNode;
    description?: React.ReactNode;
    headerEnd?: React.ReactNode;
    title?: React.ReactNode;
    variant?: 'compact' | 'default' | 'plain';
  };

export const PanelSection = React.forwardRef<HTMLElement, PanelSectionProps>(function PanelSection(
  {
    children,
    className,
    dataRole,
    description,
    headerEnd,
    title,
    variant = 'default',
    'data-role': dataRoleAttribute,
    ...passProps
  },
  ref,
) {
  const hasHeader = title !== undefined || description !== undefined || headerEnd !== undefined;

  return (
    <section
      {...passProps}
      ref={ref}
      className={block({
        element: 'section',
        modifiers: { variant },
        extra: className,
      })}
      data-role={getDataRole(dataRole, dataRoleAttribute)}
    >
      {hasHeader && (
        <Layout.Element className={block('section-header')}>
          <Layout.Element className={block('section-heading')}>
            {title !== undefined && (
              <Text
                component="h3"
                size="md"
                fontWeight="semibold"
                className={block('section-title')}
              >
                {title}
              </Text>
            )}
            {description !== undefined && (
              <Text size="sm" color="text-secondary" className={block('section-description')}>
                {description}
              </Text>
            )}
          </Layout.Element>
          {headerEnd !== undefined && (
            <Layout.Element className={block('section-header-end')}>{headerEnd}</Layout.Element>
          )}
        </Layout.Element>
      )}
      <Layout.Element className={block('section-content')}>{children}</Layout.Element>
    </section>
  );
});
